import { useState, useEffect } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { entitlementsOf } from '../../utils/entitlements'
import { trainingBlockApi } from '../../api'
import type { TrainingTagsGroupedDto } from '../../api/trainingBlockApi'
import { BLOCK_TAG_CATEGORIES } from '../../api/trainingBlockApi'
import { TagSelector } from './TagSelector'
import { BlockItemsEditor } from './BlockItemsEditor'
import { emptyItem, itemsToApi, type ItemRow } from './blockItemHelpers'
import type { ApiError } from '../../api/client'
import { Button, ExperimentalTextImportOption } from '../ui'
import { useTranslation } from '../../i18n/useTranslation'
import { useTheme } from '../../hooks/useTheme'
import { getFormErrorStyle, getInputStyle, INPUT_CLASS_NAME } from '../../utils/themeStyles'
import { captureEvent } from '../../posthog'

export default function CreateTrainingBlock() {
  const navigate = useNavigate()
  const theme = useTheme()
  const { t } = useTranslation()
  const { user } = useAuth()
  const blocked = !entitlementsOf(user).createOwnBlocksAndPrograms

  const [name, setName] = useState('')
  const [availableTags, setAvailableTags] = useState<TrainingTagsGroupedDto>({
    muscles: [], levels: [], genders: [], types: [],
  })
  const [tags, setTags] = useState<string[]>([])
  const [items, setItems] = useState<ItemRow[]>([emptyItem()])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    trainingBlockApi.getTags().then(setAvailableTags).catch(() => {})
  }, [])

  if (blocked) {
    return <Navigate to="/training-blocks" replace />
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)

    if (!name.trim()) {
      setError(t.createTrainingBlock.error.nameRequired)
      return
    }
    const payloadItems = itemsToApi(items)
    if (payloadItems.length === 0) {
      setError(t.createTrainingBlock.error.noExercises)
      return
    }
    for (const item of payloadItems) {
      if (item.exercises.length === 0) {
        setError(t.createTrainingBlock.error.exerciseNameRequired)
        return
      }
    }

    setSubmitting(true)
    try {
      await trainingBlockApi.createBlock({
        name: name.trim(),
        tags,
        items: payloadItems,
      })
      captureEvent('training_block_created', { tag_count: tags.length })
      navigate('/training-blocks')
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || t.createTrainingBlock.error.createFailed)
    } finally {
      setSubmitting(false)
    }
  }

  const itemLabels = {
    itemsLabel: t.createTrainingBlock.exercisesLabel,
    addExercise: t.createTrainingBlock.addExercise,
    addToGroup: t.createTrainingBlock.addToGroup,
    notePlaceholder: t.createTrainingBlock.notePlaceholder,
    addNote: t.createTrainingBlock.addNote,
    changeNote: t.createTrainingBlock.changeNote,
    doneNote: t.createTrainingBlock.doneNote,
    exerciseNamePlaceholder: t.createTrainingBlock.exerciseNamePlaceholder,
    groupLabel: t.createTrainingBlock.groupLabel,
    ungroup: t.createTrainingBlock.ungroup,
  }

  return (
    <div className="p-5 pb-24">
      <header className="mb-6">
        <h1 className="text-2xl font-bold mb-1" style={{ color: theme.text.primary }}>
          {t.createTrainingBlock.title}
        </h1>
      </header>

      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: theme.text.primary }}>
            {t.createTrainingBlock.nameLabel}
          </label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={t.createTrainingBlock.namePlaceholder}
            className={INPUT_CLASS_NAME}
            style={getInputStyle(theme)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: theme.text.primary }}>
            {t.createTrainingBlock.tagsLabel}
          </label>
          <TagSelector
            catalog={availableTags}
            categories={BLOCK_TAG_CATEGORIES}
            selected={tags}
            onChange={setTags}
          />
        </div>

        <BlockItemsEditor
          items={items}
          onChange={setItems}
          textColor={theme.text.primary}
          bgColor={theme.bgColor}
          labels={itemLabels}
        />

        {error && (
          <div className="rounded-xl p-4" style={getFormErrorStyle()}>
            <p className="text-sm" style={{ color: theme.text.primary }}>{error}</p>
          </div>
        )}

        <Button type="submit" variant="primary" size="lg" fullWidth disabled={submitting}>
          {submitting ? t.createTrainingBlock.submitting : t.createTrainingBlock.submit}
        </Button>

        <div className="flex items-center gap-3 my-1">
          <div className="flex-1 border-t" style={{ borderColor: theme.cardBorder }} />
          <span className="text-xs" style={{ color: theme.text.muted }}>{t.createTrainingBlock.orDivider}</span>
          <div className="flex-1 border-t" style={{ borderColor: theme.cardBorder }} />
        </div>

        <ExperimentalTextImportOption
          title={t.createTrainingBlock.createInBot}
          description={t.createTrainingBlock.createInBotHint}
          onClick={() => navigate('/training-blocks/create-from-text')}
        />
      </form>
    </div>
  )
}
